import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Building2, Users, Plus, Settings, BarChart3, Power, LogOut, LayoutDashboard, DollarSign, Activity, CircleDollarSign, Check, X, Eye, Trash2, ArrowLeft, Upload, UserPlus, AlertTriangle, MessageSquare, Snowflake, Send, CalendarDays, UserCog, KeyRound, Zap, FileText, Mail, RefreshCw, Menu as MenuIcon, Receipt, FileSpreadsheet } from 'lucide-react';
import { SubscriptionDebtsPanel } from '@/components/superadmin/SubscriptionDebtsPanel';
import { ExtraChargesPanel } from '@/components/superadmin/ExtraChargesPanel';
import { QuoteUsersPanel } from '@/components/superadmin/QuoteUsersPanel';
import { SupportTicketsPanel } from '@/components/superadmin/SupportTicketsPanel';
import { MasterCatalogPanel } from '@/components/superadmin/MasterCatalogPanel';
import { MASTER_BUSINESS_TYPES, getMasterTypeByKey, resolveMasterKey } from '@/lib/businessTypes';
import { isValidBusinessEmail, EMAIL_ERROR_TR } from '@/lib/email';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { TwoFactorSection } from '@/components/TwoFactorSection';
import { AuditLogsView } from '@/components/AuditLogsView';
import { SecurityBell } from '@/components/SecurityBell';

const typeLabels = {
  apartment: 'Apartman/Site', gym: 'Spor Salonu', accounting: 'Mali Müşavir',
  general: 'Diğer İşletme',
  barber: 'Erkek Kuaförü / Berber',
  beauty: 'Kadın Kuaförü / Güzellik',
  dental: 'Klinik / Sağlık',
};
const typeColors = {
  apartment: 'bg-blue-500', gym: 'bg-orange-500', accounting: 'bg-purple-500',
  general: 'bg-emerald-500',
  barber: 'bg-blue-600',
  beauty: 'bg-pink-500',
  dental: 'bg-cyan-600',
};
/** Master key (e.g. `psikolog`) → human label for the table & detail panel.
 *  Falls back to the coarse `typeLabels` for legacy rows. */
const labelForBusiness = (biz) => {
  const masterKey = resolveMasterKey({
    business_type: biz?.type || biz?.business_type,
    appointment_category: biz?.appointment_category,
    appointment_subcategory: biz?.appointment_subcategory,
  });
  if (masterKey) return getMasterTypeByKey(masterKey)?.label;
  return typeLabels[biz?.type || biz?.business_type] || biz?.type;
};

const PACKAGE_FEATURES = [
  { key: 'messaging', label: 'Mesajlaşma', type: 'toggle' },
  { key: 'messaging_limit', label: 'Aylık Mesaj Limiti', type: 'number' },
  { key: 'sms', label: 'SMS Gönderimi', type: 'toggle' },
  { key: 'sms_limit', label: 'Aylık SMS Limiti', type: 'number' },
  { key: 'max_users', label: 'Maks. Kullanıcı', type: 'number' },
  { key: 'appointments', label: 'Randevu Modülü', type: 'toggle' },
  { key: 'payments', label: 'Ödeme Takibi', type: 'toggle' },
  { key: 'reports', label: 'Raporlama', type: 'toggle' },
  { key: 'bulk_import', label: 'Toplu Aktarım', type: 'toggle' },
  { key: 'expenses', label: 'Gider Yönetimi', type: 'toggle' },
  { key: 'announcements', label: 'Duyuru & Karar Destek', type: 'toggle' },
];

const paymentStatusLabel = { paid: 'Ödendi', pending: 'Bekliyor', free: 'Ücretsiz' };
const paymentStatusColor = { paid: 'bg-green-100 text-green-700', pending: 'bg-amber-100 text-amber-700', free: 'bg-gray-100 text-gray-500' };

export const SuperAdminDashboard = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  const [stats, setStats] = useState({});
  const [businesses, setBusinesses] = useState([]);
  const [activityData, setActivityData] = useState([]);
  const [revenueData, setRevenueData] = useState({});
  const [revenueHistory, setRevenueHistory] = useState([]);
  const [allPayments, setAllPayments] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [packageEdit, setPackageEdit] = useState(null);
  const [feeEdit, setFeeEdit] = useState(null);
  const [paymentRecord, setPaymentRecord] = useState(null);
  const [detailView, setDetailView] = useState(null);
  const [manageBiz, setManageBiz] = useState(null); // selected business for user management
  const [bizUsers, setBizUsers] = useState([]);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [userForm, setUserForm] = useState({ email: '', name: '', password: '', phone: '', apartment_number: '', role: 'user' });

  const [form, setForm] = useState({ name: '', master_key: 'erkek_kuafor', admin_email: '', admin_password: '', admin_name: '' });
  const [payForm, setPayForm] = useState({ amount: '', period: new Date().toISOString().slice(0, 7), method: 'havale', notes: '' });
  const [contractEdit, setContractEdit] = useState(null);
  const [planSmsEdit, setPlanSmsEdit] = useState(null); // {id, name, plan, sms_balance, sms_add, sms_mode}
  const [extraChargePreset, setExtraChargePreset] = useState(null); // business_id pre-selected for ExtraCharges modal
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', currentPassword: '', newPassword: '', confirmPassword: '' });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = () => {
    fetchStats(); fetchBusinesses(); fetchActivity(); fetchRevenue(); fetchRevenueHistory(); fetchPayments();
  };

  const fetchStats = async () => { try { const { data } = await axios.get(`${API_URL}/api/superadmin/stats`, { withCredentials: true }); setStats(data); } catch (e) { /* */ } };
  const fetchBusinesses = async () => { try { const { data } = await axios.get(`${API_URL}/api/superadmin/businesses`, { withCredentials: true }); setBusinesses(data); } catch (e) { /* */ } };
  const fetchActivity = async () => { try { const { data } = await axios.get(`${API_URL}/api/superadmin/activity/overview`, { withCredentials: true }); setActivityData(data); } catch (e) { /* */ } };
  const fetchRevenue = async () => { try { const { data } = await axios.get(`${API_URL}/api/superadmin/revenue`, { withCredentials: true }); setRevenueData(data); } catch (e) { /* */ } };
  const fetchRevenueHistory = async () => { try { const { data } = await axios.get(`${API_URL}/api/superadmin/revenue/history`, { withCredentials: true }); setRevenueHistory(data); } catch (e) { /* */ } };
  const fetchPayments = async () => { try { const { data } = await axios.get(`${API_URL}/api/superadmin/revenue/payments`, { withCredentials: true }); setAllPayments(data); } catch (e) { /* */ } };

  const handleCreateBusiness = async () => {
    if (!form.name.trim() || !form.admin_email || !form.admin_password || !form.admin_name.trim()) { toast.error('Tüm alanları doldurun'); return; }
    if (!isValidBusinessEmail(form.admin_email)) { toast.error(EMAIL_ERROR_TR); return; }
    const master = getMasterTypeByKey(form.master_key);
    if (!master) { toast.error('Geçersiz işletme türü'); return; }
    try {
      await axios.post(`${API_URL}/api/superadmin/businesses`, {
        name: form.name,
        type: master.backendType,
        appointment_category: master.apptCategory,
        appointment_subcategory: master.apptSubcat,
        labels: master.labels,
        admin_email: form.admin_email,
        admin_password: form.admin_password,
        admin_name: form.admin_name,
      }, { withCredentials: true });
      toast.success('İşletme oluşturuldu'); setCreateOpen(false);
      setForm({ name: '', master_key: 'erkek_kuafor', admin_email: '', admin_password: '', admin_name: '' }); fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'Oluşturulamadı'); }
  };

  const handleToggleActive = async (bizId, isActive) => {
    try {
      await axios.put(`${API_URL}/api/superadmin/businesses/${bizId}`, { is_active: !isActive }, { withCredentials: true });
      toast.success(isActive ? 'Devre dışı bırakıldı' : 'Aktifleştirildi'); fetchAll();
    } catch (e) { toast.error('İşlem başarısız'); }
  };

  const handlePermanentDelete = async (bizId, bizName) => {
    const confirmed = window.prompt(`"${bizName}" işletmesini ve TÜM verilerini kalıcı olarak silmek için işletme adını yazın:`);
    if (confirmed !== bizName) { if (confirmed !== null) toast.error('İşletme adı eşleşmedi'); return; }
    try {
      await axios.delete(`${API_URL}/api/superadmin/businesses/${bizId}/permanent`, { withCredentials: true });
      toast.success('İşletme kalıcı olarak silindi');
      if (manageBiz?.business_id === bizId || manageBiz?.id === bizId) { setManageBiz(null); setActiveTab('businesses'); }
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'Silinemedi'); }
  };

  const handleUpdatePackage = async () => {
    if (!packageEdit) return;
    try {
      await axios.put(`${API_URL}/api/superadmin/businesses/${packageEdit.id}/package`, packageEdit.pkg, { withCredentials: true });
      toast.success('Paket güncellendi'); setPackageEdit(null); fetchAll();
    } catch (e) { toast.error('Güncelleme başarısız'); }
  };

  const handleSetFee = async () => {
    if (!feeEdit) return;
    try {
      await axios.put(`${API_URL}/api/superadmin/businesses/${feeEdit.id}/subscription`, { monthly_fee: parseFloat(feeEdit.fee) || 0, notes: feeEdit.notes || '' }, { withCredentials: true });
      toast.success('Abonelik ücreti güncellendi'); setFeeEdit(null); fetchAll();
    } catch (e) { toast.error('Güncelleme başarısız'); }
  };

  const handleSaveContract = async () => {
    if (!contractEdit) return;
    try {
      await axios.put(`${API_URL}/api/superadmin/businesses/${contractEdit.id}`, {
        contract_start: contractEdit.contract_start || null,
        contract_end: contractEdit.contract_end || null,
        monthly_fee: parseFloat(contractEdit.monthly_fee) || 0,
      }, { withCredentials: true });
      toast.success('Sözleşme bilgileri güncellendi');
      setContractEdit(null);
      fetchAll();
    } catch (e) { toast.error('Güncelleme başarısız'); }
  };

  const handleSavePlanSms = async () => {
    if (!planSmsEdit) return;
    try {
      // Update plan if changed
      if (planSmsEdit.plan && planSmsEdit.plan !== planSmsEdit.original_plan) {
        await axios.put(`${API_URL}/api/superadmin/businesses/${planSmsEdit.id}/plan`, { plan: planSmsEdit.plan });
      }
      // Update SMS balance if amount set
      const smsAmount = parseInt(planSmsEdit.sms_add, 10);
      if (!isNaN(smsAmount) && smsAmount !== 0) {
        await axios.put(`${API_URL}/api/superadmin/businesses/${planSmsEdit.id}/sms-balance`, {
          amount: smsAmount,
          mode: planSmsEdit.sms_mode || 'add',
        });
      } else if (planSmsEdit.sms_mode === 'set' && planSmsEdit.sms_add === '0') {
        // explicit set to zero
        await axios.put(`${API_URL}/api/superadmin/businesses/${planSmsEdit.id}/sms-balance`, { amount: 0, mode: 'set' });
      }
      toast.success('Plan & SMS güncellendi');
      setPlanSmsEdit(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Güncelleme başarısız');
    }
  };

  const handleSaveProfile = async () => {
    if (profileForm.newPassword) {
      if (profileForm.newPassword.length < 6) {
        toast.error('Yeni şifre en az 6 karakter olmalı');
        return;
      }
      if (profileForm.newPassword !== profileForm.confirmPassword) {
        toast.error('Şifreler eşleşmiyor');
        return;
      }
    }
    const payload = {};
    if (profileForm.name.trim()) payload.name = profileForm.name.trim();
    if (profileForm.newPassword) payload.password = profileForm.newPassword;
    if (!Object.keys(payload).length) {
      toast.error('Değişiklik yapılmadı');
      return;
    }
    try {
      await axios.put(`${API_URL}/api/profile`, payload, { withCredentials: true });
      toast.success('Profil güncellendi');
      setProfileOpen(false);
      setProfileForm({ name: '', currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Güncelleme başarısız');
    }
  };

  const openProfile = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/profile`, { withCredentials: true });
      setProfileForm({ name: data.name || '', currentPassword: '', newPassword: '', confirmPassword: '' });
      setProfileOpen(true);
    } catch (e) { toast.error('Profil yüklenemedi'); }
  };

  const handleRecordPayment = async () => {
    if (!paymentRecord || !payForm.amount) { toast.error('Tutar girin'); return; }
    try {
      await axios.post(`${API_URL}/api/superadmin/businesses/${paymentRecord.id}/revenue`, {
        amount: parseFloat(payForm.amount), period: payForm.period, method: payForm.method, notes: payForm.notes,
      }, { withCredentials: true });
      toast.success('Ödeme kaydedildi'); setPaymentRecord(null);
      setPayForm({ amount: '', period: new Date().toISOString().slice(0, 7), method: 'havale', notes: '' }); fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'Kayıt başarısız'); }
  };

  const handleDeletePayment = async (pid) => {
    if (!window.confirm('Bu ödeme kaydını silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/api/superadmin/revenue/${pid}`, { withCredentials: true });
      toast.success('Silindi'); fetchAll();
    } catch (e) { toast.error('Silinemedi'); }
  };

  const handleResetUsage = async (bizId) => {
    try {
      await axios.post(`${API_URL}/api/superadmin/businesses/${bizId}/reset-usage`, {}, { withCredentials: true });
      toast.success('Sayaçlar sıfırlandı'); fetchAll();
    } catch (e) { toast.error('İşlem başarısız'); }
  };

  const fetchDetail = async (bizId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/superadmin/businesses/${bizId}/activity`, { withCredentials: true });
      setDetailView(data);
    } catch (e) { toast.error('Detay alınamadı'); }
  };

  const openManageBiz = async (biz) => {
    setManageBiz(biz);
    setActiveTab('manage');
    await fetchBizUsers(biz.business_id || biz.id);
  };

  const fetchBizUsers = async (bizId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/superadmin/businesses/${bizId}/users`, { withCredentials: true });
      setBizUsers(data);
    } catch (e) { setBizUsers([]); }
  };

  const handleAddUser = async () => {
    const bizId = manageBiz?.business_id || manageBiz?.id;
    if (!userForm.email || !userForm.name || !userForm.password) { toast.error('Ad, e-posta ve şifre zorunlu'); return; }
    if (!isValidBusinessEmail(userForm.email)) { toast.error(EMAIL_ERROR_TR); return; }
    try {
      await axios.post(`${API_URL}/api/superadmin/businesses/${bizId}/users`, userForm, { withCredentials: true });
      toast.success('Kullanıcı eklendi');
      setAddUserOpen(false);
      setUserForm({ email: '', name: '', password: '', phone: '', apartment_number: '', role: 'user' });
      fetchBizUsers(bizId);
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'Eklenemedi'); }
  };

  const handleDeleteBizUser = async (userId) => {
    const bizId = manageBiz?.business_id || manageBiz?.id;
    if (!window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/api/superadmin/businesses/${bizId}/users/${userId}`, { withCredentials: true });
      toast.success('Kullanıcı silindi');
      fetchBizUsers(bizId);
      fetchAll();
    } catch (e) { toast.error('Silinemedi'); }
  };

  const handleBulkImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const bizId = manageBiz?.business_id || manageBiz?.id;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API_URL}/api/superadmin/businesses/${bizId}/bulk-import`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${data.success} kullanıcı eklendi${data.errors?.length ? `, ${data.errors.length} hata` : ''}`);
      fetchBizUsers(bizId);
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'İçe aktarma başarısız'); }
    e.target.value = '';
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  // Freeze/Unfreeze business
  const handleToggleFreeze = async (bizId, isFrozen) => {
    const action = isFrozen ? 'aktifleştirmek' : 'dondurmak';
    if (!window.confirm(`Bu işletmeyi ${action} istediğinizden emin misiniz?`)) return;
    try {
      await axios.put(`${API_URL}/api/superadmin/businesses/${bizId}`, { is_frozen: !isFrozen }, { withCredentials: true });
      toast.success(isFrozen ? 'Hesap aktifleştirildi' : 'Hesap donduruldu');
      fetchAll();
    } catch (e) { toast.error('İşlem başarısız'); }
  };

  // SuperAdmin messaging
  const [saMsg, setSaMsg] = useState('');
  const [saMsgTarget, setSaMsgTarget] = useState('all');
  const [saMsgBizId, setSaMsgBizId] = useState('');
  const [saSending, setSaSending] = useState(false);
  const [saSentMessages, setSaSentMessages] = useState([]);

  const fetchSentMessages = async () => {
    try { const { data } = await axios.get(`${API_URL}/api/superadmin/messages/sent`, { withCredentials: true }); setSaSentMessages(data); } catch (e) { /* */ }
  };

  const handleSendSAMessage = async () => {
    if (!saMsg.trim()) { toast.error('Mesaj yazın'); return; }
    const payload = { message: saMsg.trim() };
    if (saMsgTarget === 'all') {
      payload.send_to_all = true;
    } else if (saMsgTarget === 'business' && saMsgBizId) {
      payload.business_ids = [saMsgBizId];
    } else {
      toast.error('Hedef seçin'); return;
    }
    setSaSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/superadmin/messages`, payload, { withCredentials: true });
      toast.success(data.message);
      setSaMsg(''); fetchSentMessages();
    } catch (error) { toast.error(error.response?.data?.detail || 'Gönderilemedi'); }
    finally { setSaSending(false); }
  };

  const sidebarLinks = [
    { id: 'businesses', icon: Building2, label: 'Eski Sistem İşletmeleri' },
    { id: 'quote_users', icon: FileSpreadsheet, label: 'Teklif Kullanıcıları' },
    { id: 'master_catalog', icon: FileSpreadsheet, label: 'Master Katalog' },
    { id: 'support_tickets', icon: MessageSquare, label: 'Destek Talepleri' },
    { id: 'debts', icon: Receipt, label: 'Eski Sistem Borçları' },
    { id: 'extra_charges', icon: CircleDollarSign, label: 'Ek Ücretler' },
    { id: 'revenue', icon: DollarSign, label: 'Eski Sistem Gelirleri' },
    { id: 'activity', icon: Activity, label: 'Eski Sistem Aktivitesi' },
    { id: 'messages', icon: MessageSquare, label: 'Mesajlar' },
    { id: 'email', icon: Mail, label: 'E-posta' },
    { id: 'audit', icon: FileText, label: 'Denetim Kaydı' },
  ];
  const [activeTab, setActiveTab] = useState('businesses');

  const sidebarAll = manageBiz
    ? [{ id: 'manage', icon: Users, label: manageBiz.name || 'İşletme Yönetimi' }, ...sidebarLinks]
    : sidebarLinks;

  useEffect(() => { if (activeTab === 'messages') fetchSentMessages(); }, [activeTab]);

  // Email dispatch stats (loaded when tab opens)
  const [emailStats, setEmailStats] = useState(null);
  const [emailStatsLoading, setEmailStatsLoading] = useState(false);
  const fetchEmailStats = async () => {
    setEmailStatsLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/superadmin/email/stats?hours=24`, { withCredentials: true });
      setEmailStats(data);
    } catch (e) {
      toast.error('E-posta istatistikleri yüklenemedi');
    } finally {
      setEmailStatsLoading(false);
    }
  };
  useEffect(() => { if (activeTab === 'email') fetchEmailStats(); }, [activeTab]);

  // Mobile sidebar drawer state
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // Auto-close drawer when tab changes
  useEffect(() => { setMobileSidebarOpen(false); }, [activeTab]);

  return (
    <div className="flex min-h-screen bg-[#F9F8F6]">
      {/* Mobile topbar */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-[#1A1A2E] text-white px-4 h-14 flex items-center justify-between border-b border-white/10" data-testid="sa-mobile-topbar">
        <button onClick={() => setMobileSidebarOpen(true)} className="p-2 -ml-2 hover:bg-white/10 rounded-lg" data-testid="sa-mobile-menu-btn" aria-label="Menüyü aç">
          <MenuIcon className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <div className="bg-amber-500 p-1.5 rounded-md"><LayoutDashboard className="w-4 h-4 text-white" /></div>
          <span className="text-sm font-bold">Sistem Yönetimi</span>
        </div>
        <SecurityBell />
      </header>

      {/* Mobile drawer overlay */}
      {mobileSidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/60" onClick={() => setMobileSidebarOpen(false)} data-testid="sa-mobile-overlay" />
      )}

      {/* Sidebar (mobile drawer + desktop fixed) */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-72 lg:w-64 bg-[#1A1A2E] text-white min-h-screen flex flex-col transform transition-transform lg:transform-none ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="sa-sidebar"
      >
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="bg-amber-500 p-2 rounded-lg shrink-0"><LayoutDashboard className="w-5 h-5 text-white" /></div>
              <div className="min-w-0"><p className="text-sm font-bold">Sistem Yönetimi</p><p className="text-xs text-white/60">Super Admin</p></div>
            </div>
            <div className="hidden lg:block"><SecurityBell /></div>
            <button className="lg:hidden p-2 -mr-2 hover:bg-white/10 rounded-lg" onClick={() => setMobileSidebarOpen(false)} data-testid="sa-mobile-close-btn" aria-label="Menüyü kapat">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {sidebarAll.map((link) => {
            const Icon = link.icon;
            return (
              <button key={link.id} onClick={() => { setActiveTab(link.id); if (link.id !== 'manage') setManageBiz(null); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-sm ${activeTab === link.id ? 'bg-white/10 text-white' : 'text-white/60 hover:bg-white/5 hover:text-white'}`}
                data-testid={`sa-nav-${link.id}`}>
                <Icon className="w-5 h-5 shrink-0" /><span className="truncate">{link.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-2">
          <p className="text-xs text-white/60 px-4 mb-2 truncate" data-testid="sa-current-email">{user?.email || 'info@paydaai.com'}</p>
          <Button onClick={openProfile} variant="outline" className="w-full justify-start gap-3 text-white border-white/20 hover:bg-white/10" data-testid="sa-profile-btn">
            <UserCog className="w-4 h-4" /> Profil & Şifre
          </Button>
          <Button onClick={handleLogout} variant="outline" className="w-full justify-start gap-3 text-white border-white/20 hover:bg-white/10" data-testid="sa-logout">
            <LogOut className="w-4 h-4" /> Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto pt-20 lg:pt-8">
        <div className="max-w-7xl mx-auto">

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
            <Card className="border border-[#E5E5E5]"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-[#1A1A2E]" data-testid="stat-businesses">{stats.total_businesses || 0}</p><p className="text-xs text-[#595959]">İşletme</p></CardContent></Card>
            <Card className="border border-[#E5E5E5]"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-green-600" data-testid="stat-active">{stats.active_businesses || 0}</p><p className="text-xs text-[#595959]">Aktif</p></CardContent></Card>
            <Card className="border border-[#E5E5E5]"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-blue-600" data-testid="stat-users">{stats.total_users || 0}</p><p className="text-xs text-[#595959]">Kullanıcı</p></CardContent></Card>
            <Card className="border border-[#E5E5E5]"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-emerald-600" data-testid="stat-collected">{(stats.monthly_collected || 0).toLocaleString('tr-TR')} TL</p><p className="text-xs text-[#595959]">Alınan</p></CardContent></Card>
            <Card className="border border-[#E5E5E5]"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-amber-600" data-testid="stat-pending-revenue">{(stats.monthly_pending || 0).toLocaleString('tr-TR')} TL</p><p className="text-xs text-[#595959]">Bekleyen</p></CardContent></Card>
            <Card className="border border-[#E5E5E5]"><CardContent className="p-3 text-center"><p className="text-xl font-bold text-[#1A1A2E]" data-testid="stat-expected">{(stats.monthly_expected || 0).toLocaleString('tr-TR')} TL</p><p className="text-xs text-[#595959]">Aylık Toplam</p></CardContent></Card>
          </div>

          {/* ===== BUSINESSES TAB ===== */}
          {activeTab === 'businesses' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-bold tracking-tight text-[#1A1A2E]" data-testid="sa-title">İşletmeler</h1>
                <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#1A1A2E] hover:bg-[#16213E] text-white gap-2" data-testid="create-business-btn"><Plus className="w-4 h-4" /> Yeni İşletme</Button>
                  </DialogTrigger>
                  <DialogContent data-testid="create-business-dialog">
                    <DialogHeader><DialogTitle>Yeni İşletme Oluştur</DialogTitle><DialogDescription>İşletme bilgilerini ve admin hesabını girin.</DialogDescription></DialogHeader>
                    <div className="space-y-4">
                      <div><Label>İşletme Adı</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Örn: Mehmet Kuaför" data-testid="biz-name-input" /></div>
                      <div><Label>İşletme Türü</Label>
                        <Select value={form.master_key} onValueChange={(v) => setForm({ ...form, master_key: v })}>
                          <SelectTrigger data-testid="biz-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {MASTER_BUSINESS_TYPES.map((t) => (
                              <SelectItem key={t.key} value={t.key} data-testid={`biz-type-opt-${t.key}`}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="border-t pt-3 space-y-3">
                        <p className="text-sm font-medium">Admin Hesabı</p>
                        <div><Label>Ad Soyad</Label><Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} data-testid="biz-admin-name" /></div>
                        <div><Label>E-posta</Label><Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} data-testid="biz-admin-email" /></div>
                        <div><Label>Şifre</Label><Input type="password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} data-testid="biz-admin-password" /></div>
                      </div>
                      <Button onClick={handleCreateBusiness} className="w-full bg-[#1A1A2E] hover:bg-[#16213E] text-white" data-testid="submit-create-business">İşletme Oluştur</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="bg-white border border-[#E5E5E5] rounded-xl" data-testid="business-table">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>İşletme</TableHead><TableHead>Tür</TableHead><TableHead>Admin</TableHead>
                      <TableHead className="text-center">Plan / SMS</TableHead>
                      <TableHead className="text-center">Kullanıcı</TableHead><TableHead className="text-right">Aylık Ücret</TableHead>
                      <TableHead className="text-center">Ödeme</TableHead><TableHead>Durum</TableHead><TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activityData.length === 0 ? (
                      <TableRow><TableCell colSpan={9} className="text-center py-8 text-[#595959]">İşletme yok</TableCell></TableRow>
                    ) : activityData.map((biz) => (
                      <TableRow key={biz.business_id} data-testid={`biz-row-${biz.business_id}`}>
                        <TableCell className="font-medium">{biz.name}</TableCell>
                        <TableCell><span className={`${typeColors[biz.type] || 'bg-gray-500'} text-white text-xs px-2 py-0.5 rounded-full`}>{labelForBusiness(biz)}</span></TableCell>
                        <TableCell className="text-sm text-[#595959]">{businesses.find(b => b.id === biz.business_id)?.owner_email || '-'}</TableCell>
                        <TableCell className="text-center">
                          {(() => {
                            const bd = businesses.find(b => b.id === biz.business_id);
                            const plan = bd?.plan || 'randevu';
                            const smsBal = bd?.sms_balance || 0;
                            const planColorMap = { mini: 'bg-slate-100 text-slate-700', pro: 'bg-blue-100 text-blue-700', premium: 'bg-purple-100 text-purple-700' };
                            return (
                              <div className="flex flex-col items-center gap-0.5">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${planColorMap[plan] || 'bg-slate-100 text-slate-700'}`} data-testid={`plan-badge-${biz.business_id}`}>
                                  {bd?.plan_label || plan}
                                </span>
                                <span className={`text-[10px] ${smsBal < 10 ? 'text-amber-600 font-semibold' : 'text-[#999]'}`} data-testid={`sms-badge-${biz.business_id}`}>
                                  SMS: {smsBal.toLocaleString('tr-TR')}
                                </span>
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-center">{biz.user_count}</TableCell>
                        <TableCell className="text-right font-medium">{(() => { const bd = businesses.find(b => b.id === biz.business_id); return bd?.monthly_fee > 0 ? `${bd.monthly_fee.toLocaleString('tr-TR')} TL` : biz.monthly_fee > 0 ? `${biz.monthly_fee.toLocaleString('tr-TR')} TL` : '-'; })()}</TableCell>
                        <TableCell className="text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${paymentStatusColor[biz.payment_status]}`}>{paymentStatusLabel[biz.payment_status]}</span></TableCell>
                        <TableCell>
                          {(() => {
                            const bizData = businesses.find(b => b.id === biz.business_id);
                            const frozen = bizData?.is_frozen;
                            const isTrial = bizData?.is_trial;
                            const trialDays = bizData?.trial_days_remaining;
                            const trialStatus = bizData?.trial_status;
                            // Trial expired → prominent red
                            if (trialStatus === 'trial_expired') {
                              return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700" data-testid={`status-trial-expired-${biz.business_id}`}>Demo Bitti · Ödeme Bekliyor</span>;
                            }
                            // On trial (countdown)
                            if (isTrial) {
                              const cls = trialDays <= 3 ? 'bg-red-100 text-red-700'
                                        : trialDays <= 7 ? 'bg-amber-100 text-amber-700'
                                        : 'bg-blue-100 text-blue-700';
                              return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`} data-testid={`status-trial-${biz.business_id}`}>Demo · {trialDays} gün kaldı</span>;
                            }
                            const contractEnd = bizData?.contract_end;
                            const daysLeft = contractEnd ? Math.ceil((new Date(contractEnd) - new Date()) / 86400000) : null;
                            if (frozen) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700" data-testid={`status-frozen-${biz.business_id}`}>{bizData?.frozen_reason === 'contract_expired' ? 'Süresi Doldu' : 'Donduruldu'}</span>;
                            if (daysLeft !== null && daysLeft <= 5) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">{daysLeft} gün kaldı</span>;
                            if (daysLeft !== null && daysLeft <= 15) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">{daysLeft} gün kaldı</span>;
                            if (contractEnd) return <span className="text-xs text-[#595959]">{contractEnd}</span>;
                            return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${biz.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{biz.is_active ? 'Aktif' : 'Pasif'}</span>;
                          })()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-0.5">
                            <Button variant="ghost" size="sm" onClick={() => openManageBiz(biz)} title="Kullanıcı Yönet" data-testid={`manage-${biz.business_id}`}><Users className="w-4 h-4 text-indigo-600" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => fetchDetail(biz.business_id)} title="Detay" data-testid={`detail-${biz.business_id}`}><Eye className="w-4 h-4 text-blue-600" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => setFeeEdit({ id: biz.business_id, fee: biz.monthly_fee || '', notes: '' })} title="Ücret Belirle" data-testid={`fee-${biz.business_id}`}><CircleDollarSign className="w-4 h-4 text-emerald-600" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => { setExtraChargePreset(biz.business_id); setActiveTab('extra_charges'); }} title="Ek Ücret Tanımla" data-testid={`extra-charge-${biz.business_id}`}><Receipt className="w-4 h-4 text-blue-600" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => { setPaymentRecord({ id: biz.business_id, name: biz.name }); setPayForm({ ...payForm, amount: String(biz.monthly_fee || '') }); }} title="Ödeme Kaydet" data-testid={`record-pay-${biz.business_id}`}><Check className="w-4 h-4 text-green-600" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => setPackageEdit({ id: biz.business_id, pkg: { ...(businesses.find(b => b.id === biz.business_id)?.package || {}) } })} title="Paket" data-testid={`pkg-edit-${biz.business_id}`}><Settings className="w-4 h-4 text-violet-600" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => { const bd = businesses.find(b => b.id === biz.business_id); setPlanSmsEdit({ id: biz.business_id, name: biz.name, plan: bd?.plan || 'randevu', original_plan: bd?.plan || 'randevu', sms_balance: bd?.sms_balance || 0, sms_add: '', sms_mode: 'add' }); }} title="Plan & SMS" data-testid={`plan-sms-${biz.business_id}`}><Zap className="w-4 h-4 text-amber-600" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => { const bd = businesses.find(b => b.id === biz.business_id); setContractEdit({ id: biz.business_id, name: biz.name, contract_start: bd?.contract_start || '', contract_end: bd?.contract_end || '', monthly_fee: bd?.monthly_fee || '' }); }} title="Sözleşme" data-testid={`contract-${biz.business_id}`}><CalendarDays className="w-4 h-4 text-orange-600" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => { const bizData = businesses.find(b => b.id === biz.business_id); handleToggleFreeze(biz.business_id, bizData?.is_frozen); }} title={businesses.find(b => b.id === biz.business_id)?.is_frozen ? 'Çöz' : 'Dondur'} data-testid={`freeze-${biz.business_id}`}><Snowflake className={`w-4 h-4 ${businesses.find(b => b.id === biz.business_id)?.is_frozen ? 'text-blue-500' : 'text-gray-400'}`} /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleToggleActive(biz.business_id, biz.is_active)} title={biz.is_active ? 'Devre Dışı' : 'Aktifleştir'} data-testid={`toggle-${biz.business_id}`}><Power className={`w-4 h-4 ${biz.is_active ? 'text-red-500' : 'text-green-500'}`} /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handlePermanentDelete(biz.business_id, biz.name)} title="Kalıcı Sil" data-testid={`delete-biz-${biz.business_id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* ===== QUOTE USERS TAB ===== */}
          {activeTab === 'quote_users' && (
            <QuoteUsersPanel />
          )}

          {/* ===== MASTER CATALOG TAB ===== */}
          {activeTab === 'master_catalog' && (
            <MasterCatalogPanel />
          )}

          {/* ===== SUPPORT TICKETS TAB ===== */}
          {activeTab === 'support_tickets' && (
            <SupportTicketsPanel />
          )}

          {/* ===== DEBTS TAB ===== */}
          {activeTab === 'debts' && (
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-[#1A1A2E] mb-2" data-testid="debts-title">Abonelik Borçları</h1>
              <p className="text-sm text-[#595959] mb-6">İşletmelerin Paydaai'a ödemekle yükümlü olduğu bekleyen ve gecikmiş aidatlar.</p>
              <SubscriptionDebtsPanel />
            </div>
          )}

          {/* ===== EXTRA CHARGES TAB ===== */}
          {activeTab === 'extra_charges' && (
            <ExtraChargesPanel
              businesses={businesses}
              presetBusinessId={extraChargePreset}
              onModalClose={() => setExtraChargePreset(null)}
            />
          )}

          {/* ===== REVENUE TAB ===== */}
          {activeTab === 'revenue' && (
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-[#1A1A2E] mb-6" data-testid="revenue-title">Gelir Takibi</h1>

              {/* Revenue Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card className="border-2 border-emerald-200 bg-emerald-50"><CardContent className="p-5"><p className="text-sm text-emerald-700 mb-1">Bu Ay Alınan</p><p className="text-3xl font-bold text-emerald-700" data-testid="rev-collected">{(revenueData.total_collected || 0).toLocaleString('tr-TR')} TL</p></CardContent></Card>
                <Card className="border-2 border-amber-200 bg-amber-50"><CardContent className="p-5"><p className="text-sm text-amber-700 mb-1">Bekleyen</p><p className="text-3xl font-bold text-amber-700" data-testid="rev-pending">{(revenueData.total_pending || 0).toLocaleString('tr-TR')} TL</p></CardContent></Card>
                <Card className="border-2 border-[#E5E5E5]"><CardContent className="p-5"><p className="text-sm text-[#595959] mb-1">Aylık Beklenen Toplam</p><p className="text-3xl font-bold text-[#1A1A2E]" data-testid="rev-expected">{(revenueData.total_expected || 0).toLocaleString('tr-TR')} TL</p></CardContent></Card>
              </div>

              {/* Pending Payments */}
              {revenueData.pending_businesses?.length > 0 && (
                <Card className="border border-amber-200 mb-6" data-testid="pending-payments-card">
                  <CardHeader><CardTitle className="text-lg text-amber-700">Ödeme Bekleyenler ({revenueData.current_period})</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {revenueData.pending_businesses.map((pb) => (
                        <div key={pb.business_id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                          <div><p className="font-medium text-sm">{pb.business_name}</p><p className="text-xs text-[#595959]">{pb.monthly_fee.toLocaleString('tr-TR')} TL</p></div>
                          <Button size="sm" className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white gap-1" onClick={() => { setPaymentRecord({ id: pb.business_id, name: pb.business_name }); setPayForm({ amount: String(pb.monthly_fee), period: pb.period, method: 'havale', notes: '' }); }}>
                            <Check className="w-3 h-3" /> Ödeme Al
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Revenue History */}
              <Card className="border border-[#E5E5E5] mb-6">
                <CardHeader><CardTitle className="text-lg">Aylık Gelir Geçmişi</CardTitle></CardHeader>
                <CardContent>
                  {revenueHistory.length === 0 ? (
                    <p className="text-[#595959] text-sm text-center py-4">Henüz gelir kaydı yok</p>
                  ) : (
                    <div className="space-y-2">
                      {revenueHistory.map((rh) => (
                        <div key={rh.period} className="flex items-center justify-between p-3 border rounded-lg">
                          <div><p className="font-medium text-sm">{rh.period}</p><p className="text-xs text-[#595959]">{rh.count} ödeme</p></div>
                          <p className="font-bold text-emerald-700">{rh.total.toLocaleString('tr-TR')} TL</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* All Payment Records */}
              <div className="bg-white border border-[#E5E5E5] rounded-xl" data-testid="payment-records-table">
                <Table>
                  <TableHeader><TableRow><TableHead>İşletme</TableHead><TableHead>Dönem</TableHead><TableHead>Tutar</TableHead><TableHead>Yöntem</TableHead><TableHead>Not</TableHead><TableHead>Tarih</TableHead><TableHead className="text-right">Sil</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {allPayments.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-6 text-[#595959]">Ödeme kaydı yok</TableCell></TableRow>
                    ) : allPayments.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">{p.business_name}</TableCell>
                        <TableCell>{p.period}</TableCell>
                        <TableCell className="font-medium text-emerald-700">{p.amount?.toLocaleString('tr-TR')} TL</TableCell>
                        <TableCell className="text-sm">{p.method}</TableCell>
                        <TableCell className="text-sm text-[#595959]">{p.notes || '-'}</TableCell>
                        <TableCell className="text-xs text-[#595959]">{p.created_at ? new Date(p.created_at).toLocaleDateString('tr-TR') : '-'}</TableCell>
                        <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => handleDeletePayment(p.id)}><X className="w-4 h-4 text-red-500" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* ===== ACTIVITY TAB ===== */}
          {activeTab === 'activity' && (
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-[#1A1A2E] mb-6" data-testid="activity-title">Aktivite Takibi</h1>
              <div className="bg-white border border-[#E5E5E5] rounded-xl" data-testid="activity-table">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>İşletme</TableHead><TableHead>Tür</TableHead><TableHead className="text-center">Kullanıcı</TableHead>
                    <TableHead className="text-center">Randevu (Ay)</TableHead><TableHead className="text-center">Ödeme (Ay)</TableHead>
                    <TableHead className="text-center">Mesaj (Ay)</TableHead><TableHead className="text-center">Skor</TableHead>
                    <TableHead className="text-right">Detay</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {activityData.map((biz) => (
                      <TableRow key={biz.business_id} data-testid={`activity-row-${biz.business_id}`}>
                        <TableCell className="font-medium">{biz.name}</TableCell>
                        <TableCell><span className={`${typeColors[biz.type] || 'bg-gray-500'} text-white text-xs px-2 py-0.5 rounded-full`}>{labelForBusiness({ type: biz.type, appointment_category: businesses.find(b => b.id === biz.business_id)?.appointment_category, appointment_subcategory: businesses.find(b => b.id === biz.business_id)?.appointment_subcategory })}</span></TableCell>
                        <TableCell className="text-center">{biz.user_count}</TableCell>
                        <TableCell className="text-center">{biz.appointments_this_month}</TableCell>
                        <TableCell className="text-center">{biz.payments_this_month}</TableCell>
                        <TableCell className="text-center">{biz.messages_this_month}</TableCell>
                        <TableCell className="text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${biz.activity_score > 5 ? 'bg-green-100 text-green-700' : biz.activity_score > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>{biz.activity_score}</span></TableCell>
                        <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => fetchDetail(biz.business_id)}><Eye className="w-4 h-4 text-blue-600" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* ===== MANAGE BUSINESS TAB ===== */}
          {activeTab === 'manage' && manageBiz && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Button variant="ghost" size="sm" onClick={() => { setActiveTab('businesses'); setManageBiz(null); }} data-testid="back-to-list"><ArrowLeft className="w-4 h-4" /></Button>
                <div>
                  <h1 className="text-3xl font-bold tracking-tight text-[#1A1A2E]" data-testid="manage-title">{manageBiz.name}</h1>
                  <p className="text-sm text-[#595959]">Kullanıcı yönetimi - <span className={`${typeColors[manageBiz.type] || 'bg-gray-500'} text-white text-xs px-2 py-0.5 rounded-full`}>{typeLabels[manageBiz.type]}</span></p>
                </div>
                <div className="ml-auto flex gap-2">
                  <label className="cursor-pointer">
                    <input type="file" accept=".csv,.xlsx,.xls" onChange={handleBulkImport} className="hidden" data-testid="bulk-import-input" />
                    <Button variant="outline" className="gap-2" asChild><span><Upload className="w-4 h-4" /> Toplu Aktar</span></Button>
                  </label>
                  <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#1A1A2E] hover:bg-[#16213E] text-white gap-2" data-testid="add-user-btn"><UserPlus className="w-4 h-4" /> Kullanıcı Ekle</Button>
                    </DialogTrigger>
                    <DialogContent data-testid="add-user-dialog">
                      <DialogHeader><DialogTitle>Kullanıcı / Admin Ekle</DialogTitle><DialogDescription>{manageBiz.name} işletmesine yeni kullanıcı veya admin</DialogDescription></DialogHeader>
                      <div className="space-y-3">
                        <div>
                          <Label>Rol</Label>
                          <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                            <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">Kullanıcı</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div><Label>Ad Soyad</Label><Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} data-testid="user-name-input" /></div>
                        <div><Label>E-posta</Label><Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} data-testid="user-email-input" /></div>
                        <div><Label>Şifre</Label><Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} data-testid="user-password-input" /></div>
                        <div><Label>Telefon</Label><Input value={userForm.phone} onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })} data-testid="user-phone-input" /></div>
                        <Button onClick={handleAddUser} className="w-full bg-[#1A1A2E] hover:bg-[#16213E] text-white" data-testid="submit-add-user">Ekle</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              {/* User count card */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <Card className="border border-[#E5E5E5]"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#1A1A2E]" data-testid="biz-user-count">{bizUsers.filter(u => u.role === 'user').length}</p><p className="text-xs text-[#595959]">Kullanıcı</p></CardContent></Card>
                <Card className="border border-[#E5E5E5]"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-blue-600">{bizUsers.filter(u => u.role === 'admin').length}</p><p className="text-xs text-[#595959]">Admin</p></CardContent></Card>
              </div>

              {/* Users Table */}
              <div className="bg-white border border-[#E5E5E5] rounded-xl" data-testid="biz-users-table">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Ad Soyad</TableHead><TableHead>E-posta</TableHead><TableHead>Telefon</TableHead><TableHead>Rol</TableHead><TableHead>Kayıt Tarihi</TableHead><TableHead className="text-right">İşlem</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {bizUsers.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#595959]">Kullanıcı yok</TableCell></TableRow>
                    ) : bizUsers.map((u) => (
                      <TableRow key={u.id} data-testid={`biz-user-${u.id}`}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell className="text-sm">{u.email}</TableCell>
                        <TableCell className="text-sm">{u.phone || '-'}</TableCell>
                        <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{u.role === 'admin' ? 'Admin' : 'Kullanıcı'}</span></TableCell>
                        <TableCell className="text-xs text-[#595959]">{u.created_at ? new Date(u.created_at).toLocaleDateString('tr-TR') : '-'}</TableCell>
                        <TableCell className="text-right">
                          {(() => {
                            const bizId = manageBiz?.business_id || manageBiz?.id;
                            const bizData = businesses.find(b => b.id === bizId);
                            const isOwner = bizData?.owner_id === u.id;
                            return !isOwner ? (
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteBizUser(u.id)} data-testid={`del-user-${u.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                            ) : (
                              <span className="text-xs text-[#595959] italic px-2" data-testid={`owner-badge-${u.id}`}>Sahip</span>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Per-business activity timeline */}
              <div className="mt-8">
                <h2 className="text-xl font-bold text-[#1A1A2E] mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5" /> Aktivite Geçmişi
                </h2>
                <AuditLogsView businessId={manageBiz.business_id || manageBiz.id} />
              </div>
            </div>
          )}

          {/* ===== MESSAGES TAB ===== */}
          {activeTab === 'audit' && (
            <div data-testid="audit-tab-content">
              <AuditLogsView />
            </div>
          )}

          {/* ===== EMAIL DISPATCH STATS TAB ===== */}
          {activeTab === 'email' && (
            <div data-testid="sa-email-tab">
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-bold tracking-tight text-[#1A1A2E]">E-posta Gönderimi</h1>
                <Button variant="outline" size="sm" onClick={fetchEmailStats} disabled={emailStatsLoading} data-testid="sa-email-refresh-btn">
                  <RefreshCw className={`w-4 h-4 mr-2 ${emailStatsLoading ? 'animate-spin' : ''}`} /> Yenile
                </Button>
              </div>

              {emailStats ? (
                <>
                  {/* Top summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <Card className="border border-[#E5E5E5]"><CardContent className="p-4">
                      <p className="text-xs text-[#595959] mb-1">Son 24 saat</p>
                      <p className="text-2xl font-bold text-[#1A1A2E]" data-testid="email-stat-total">{emailStats.totals.total}</p>
                      <p className="text-xs text-[#999] mt-1">Toplam gönderim</p>
                    </CardContent></Card>
                    <Card className="border border-[#E5E5E5]"><CardContent className="p-4">
                      <p className="text-xs text-[#595959] mb-1">Başarılı</p>
                      <p className="text-2xl font-bold text-green-600" data-testid="email-stat-sent">{emailStats.totals.sent}</p>
                      <p className="text-xs text-green-700 mt-1">{emailStats.totals.success_rate}% oranla</p>
                    </CardContent></Card>
                    <Card className="border border-[#E5E5E5]"><CardContent className="p-4">
                      <p className="text-xs text-[#595959] mb-1">Başarısız</p>
                      <p className="text-2xl font-bold text-red-600" data-testid="email-stat-failed">{emailStats.totals.failed}</p>
                      <p className="text-xs text-[#999] mt-1">Retry + fallback sonrası</p>
                    </CardContent></Card>
                    <Card className={`border ${emailStats.daily_limit_usage.near_limit ? 'border-amber-400 bg-amber-50' : 'border-[#E5E5E5]'}`}><CardContent className="p-4">
                      <p className="text-xs text-[#595959] mb-1">Günlük Kota</p>
                      <p className="text-2xl font-bold text-[#1A1A2E]" data-testid="email-stat-quota">{emailStats.daily_limit_usage.sent_last_24h} / {emailStats.daily_limit_usage.limit}</p>
                      <p className={`text-xs mt-1 ${emailStats.daily_limit_usage.near_limit ? 'text-amber-700' : 'text-[#999]'}`}>
                        %{emailStats.daily_limit_usage.percent_used} kullanıldı{emailStats.daily_limit_usage.near_limit ? ' — Limit yakın!' : ''}
                      </p>
                    </CardContent></Card>
                  </div>

                  {/* By alias */}
                  <Card className="border border-[#E5E5E5] mb-6">
                    <CardHeader><CardTitle className="text-lg text-[#1A1A2E] flex items-center gap-2"><Mail className="w-5 h-5" /> Alias Dağılımı</CardTitle></CardHeader>
                    <CardContent>
                      {emailStats.by_alias.length === 0 ? (
                        <p className="text-sm text-[#999]">Son 24 saatte gönderim yok.</p>
                      ) : (
                        <Table>
                          <TableHeader><TableRow>
                            <TableHead>Gönderen</TableHead>
                            <TableHead className="text-right">Toplam</TableHead>
                            <TableHead className="text-right">Başarılı</TableHead>
                            <TableHead className="text-right">Başarısız</TableHead>
                            <TableHead className="text-right">Ort. Süre (ms)</TableHead>
                          </TableRow></TableHeader>
                          <TableBody>
                            {emailStats.by_alias.map((row) => (
                              <TableRow key={row.from_email} data-testid={`email-alias-row-${row.from_email}`}>
                                <TableCell className="font-medium">{row.from_email}</TableCell>
                                <TableCell className="text-right">{row.count}</TableCell>
                                <TableCell className="text-right text-green-600">{row.success}</TableCell>
                                <TableCell className="text-right text-red-600">{row.failed}</TableCell>
                                <TableCell className="text-right text-[#595959]">{row.avg_duration_ms ?? '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </CardContent>
                  </Card>

                  {/* By type */}
                  <Card className="border border-[#E5E5E5] mb-6">
                    <CardHeader><CardTitle className="text-lg text-[#1A1A2E]">Mail Tipine Göre</CardTitle></CardHeader>
                    <CardContent>
                      {emailStats.by_type.length === 0 ? (
                        <p className="text-sm text-[#999]">Kayıt yok.</p>
                      ) : (
                        <Table>
                          <TableHeader><TableRow>
                            <TableHead>Mail Tipi</TableHead>
                            <TableHead className="text-right">Toplam</TableHead>
                            <TableHead className="text-right">Başarılı</TableHead>
                            <TableHead className="text-right">Başarısız</TableHead>
                          </TableRow></TableHeader>
                          <TableBody>
                            {emailStats.by_type.map((row) => (
                              <TableRow key={row.email_type}>
                                <TableCell className="font-medium">{row.email_type}</TableCell>
                                <TableCell className="text-right">{row.count}</TableCell>
                                <TableCell className="text-right text-green-600">{row.success}</TableCell>
                                <TableCell className="text-right text-red-600">{row.failed}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </CardContent>
                  </Card>

                  {/* Recent */}
                  <Card className="border border-[#E5E5E5]">
                    <CardHeader><CardTitle className="text-lg text-[#1A1A2E]">Son 20 Gönderim</CardTitle></CardHeader>
                    <CardContent>
                      {emailStats.recent.length === 0 ? (
                        <p className="text-sm text-[#999]">Henüz gönderim yok.</p>
                      ) : (
                        <Table>
                          <TableHeader><TableRow>
                            <TableHead>Tarih</TableHead>
                            <TableHead>Tip</TableHead>
                            <TableHead>Gönderen</TableHead>
                            <TableHead>Alıcı</TableHead>
                            <TableHead>Durum</TableHead>
                            <TableHead className="text-right">Süre</TableHead>
                          </TableRow></TableHeader>
                          <TableBody>
                            {emailStats.recent.map((r, i) => (
                              <TableRow key={i}>
                                <TableCell className="text-xs text-[#595959]">{new Date(r.created_at).toLocaleString('tr-TR')}</TableCell>
                                <TableCell className="text-xs">{r.email_type}</TableCell>
                                <TableCell className="text-xs">{r.from_email}</TableCell>
                                <TableCell className="text-xs">{r.to_email}</TableCell>
                                <TableCell className="text-xs">
                                  {r.status === 'success' ? (
                                    <span className="inline-flex items-center gap-1 text-green-600"><Check className="w-3 h-3" /> {r.provider}</span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-red-600" title={r.error}><X className="w-3 h-3" /> {r.status}</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-xs text-right">{r.duration_ms ? `${r.duration_ms}ms` : '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </CardContent>
                  </Card>
                </>
              ) : (
                <p className="text-sm text-[#595959]" data-testid="email-stats-loading">Yükleniyor...</p>
              )}
            </div>
          )}

          {activeTab === 'messages' && (
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-[#1A1A2E] mb-6" data-testid="sa-messages-title">Admin Mesajları</h1>

              <Card className="border border-[#E5E5E5] mb-6" data-testid="sa-send-message-card">
                <CardHeader><CardTitle className="text-lg text-[#1A1A2E] flex items-center gap-2"><Send className="w-5 h-5" /> Mesaj Gönder</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-sm text-[#595959]">Hedef</Label>
                    <Select value={saMsgTarget} onValueChange={(v) => setSaMsgTarget(v)}>
                      <SelectTrigger data-testid="sa-msg-target"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tüm Adminler</SelectItem>
                        <SelectItem value="business">Belirli İşletme</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {saMsgTarget === 'business' && (
                    <div>
                      <Label className="text-sm text-[#595959]">İşletme Seçin</Label>
                      <Select value={saMsgBizId} onValueChange={(v) => setSaMsgBizId(v)}>
                        <SelectTrigger data-testid="sa-msg-biz-select"><SelectValue placeholder="İşletme seçin" /></SelectTrigger>
                        <SelectContent>
                          {businesses.map(b => (
                            <SelectItem key={b.id} value={b.id}>{b.name} ({labelForBusiness(b)})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <Label className="text-sm text-[#595959]">Mesaj</Label>
                    <Textarea value={saMsg} onChange={(e) => setSaMsg(e.target.value)} placeholder="Adminlere gönderilecek mesajınızı yazın..." rows={4} data-testid="sa-msg-input" />
                  </div>

                  <Button onClick={handleSendSAMessage} disabled={saSending || !saMsg.trim()} className="bg-[#1A1A2E] hover:bg-[#16213E] text-white gap-2 w-full h-11" data-testid="sa-send-msg-btn">
                    <Send className="w-4 h-4" />
                    {saSending ? 'Gönderiliyor...' : saMsgTarget === 'all' ? 'Tüm Adminlere Gönder' : 'Seçili İşletme Adminlerine Gönder'}
                  </Button>
                </CardContent>
              </Card>

              {/* Sent Messages */}
              <Card className="border border-[#E5E5E5]" data-testid="sa-sent-messages-card">
                <CardHeader><CardTitle className="text-lg text-[#1A1A2E]">Gönderilen Mesajlar</CardTitle></CardHeader>
                <CardContent>
                  {saSentMessages.length === 0 ? (
                    <p className="text-[#595959] text-sm text-center py-4">Henüz mesaj gönderilmemiş</p>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {saSentMessages.slice(0, 50).map((msg) => (
                        <div key={msg.id} className="p-3 border rounded-lg" data-testid={`sa-sent-${msg.id}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-[#1A1A2E]">{msg.target_name || msg.target_email}</span>
                            <span className="text-xs text-[#595959]">{msg.created_at ? new Date(msg.created_at).toLocaleDateString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                          </div>
                          <p className="text-sm text-[#595959]">{msg.message}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${msg.read ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{msg.read ? 'Okundu' : 'Okunmadı'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* ===== DIALOGS ===== */}

          {/* Package Edit Dialog */}
          <Dialog open={!!packageEdit} onOpenChange={(open) => !open && setPackageEdit(null)}>
            <DialogContent className="max-w-md" data-testid="package-edit-dialog">
              <DialogHeader><DialogTitle>Paket Ayarları</DialogTitle><DialogDescription>Özellikleri açıp kapatın ve limitleri belirleyin.</DialogDescription></DialogHeader>
              {packageEdit && (
                <div className="space-y-3">
                  {PACKAGE_FEATURES.map((feat) => (
                    <div key={feat.key} className="flex items-center justify-between">
                      <Label className="text-sm">{feat.label}</Label>
                      {feat.type === 'toggle' ? (
                        <Switch checked={packageEdit.pkg[feat.key] || false} onCheckedChange={(v) => setPackageEdit({ ...packageEdit, pkg: { ...packageEdit.pkg, [feat.key]: v } })} data-testid={`pkg-${feat.key}`} />
                      ) : (
                        <Input type="number" className="w-24 text-right" value={packageEdit.pkg[feat.key] || 0} onChange={(e) => setPackageEdit({ ...packageEdit, pkg: { ...packageEdit.pkg, [feat.key]: parseInt(e.target.value) || 0 } })} data-testid={`pkg-${feat.key}`} />
                      )}
                    </div>
                  ))}
                  <Button onClick={handleUpdatePackage} className="w-full bg-[#1A1A2E] hover:bg-[#16213E] text-white" data-testid="save-package-btn">Paketi Kaydet</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Fee Edit Dialog */}
          <Dialog open={!!feeEdit} onOpenChange={(open) => !open && setFeeEdit(null)}>
            <DialogContent className="max-w-sm" data-testid="fee-edit-dialog">
              <DialogHeader><DialogTitle>Abonelik Ücreti</DialogTitle><DialogDescription>Aylık abonelik ücretini belirleyin.</DialogDescription></DialogHeader>
              {feeEdit && (
                <div className="space-y-4">
                  <div><Label>Aylık Ücret (TL)</Label><Input type="number" value={feeEdit.fee} onChange={(e) => setFeeEdit({ ...feeEdit, fee: e.target.value })} placeholder="0" data-testid="fee-amount-input" /></div>
                  <div><Label>Not</Label><Input value={feeEdit.notes} onChange={(e) => setFeeEdit({ ...feeEdit, notes: e.target.value })} placeholder="Opsiyonel" data-testid="fee-notes-input" /></div>
                  <Button onClick={handleSetFee} className="w-full bg-[#2563EB] hover:bg-[#1E4ED8] text-white" data-testid="save-fee-btn">Ücreti Kaydet</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Plan & SMS Edit Dialog */}
          <Dialog open={!!planSmsEdit} onOpenChange={(open) => !open && setPlanSmsEdit(null)}>
            <DialogContent className="max-w-md" data-testid="plan-sms-dialog">
              <DialogHeader>
                <DialogTitle>Plan & SMS Yönetimi</DialogTitle>
                <DialogDescription>{planSmsEdit?.name}</DialogDescription>
              </DialogHeader>
              {planSmsEdit && (
                <div className="space-y-5">
                  <div>
                    <Label>Abonelik Planı</Label>
                    <Select value={planSmsEdit.plan} onValueChange={(v) => setPlanSmsEdit({ ...planSmsEdit, plan: v })}>
                      <SelectTrigger data-testid="plan-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="randevu">Randevu Paketi · ₺299/ay (200 müşteri / 1.000 işlem)</SelectItem>
                        <SelectItem value="tahsilat">Tahsilat ve Takip · ₺399/ay (500 müşteri / 3.000 işlem)</SelectItem>
                        <SelectItem value="tam">Tam Paket · ₺599/ay (1.000 müşteri / 1 şube / 10.000 işlem)</SelectItem>
                        <SelectItem value="kurumsal">Kurumsal · Teklif (sınırsız)</SelectItem>
                      </SelectContent>
                    </Select>
                    {planSmsEdit.plan !== planSmsEdit.original_plan && (
                      <p className="text-xs text-amber-700 mt-1" data-testid="plan-change-warn">
                        ⚠ Plan değişikliğinde aylık ücret otomatik güncellenir.
                      </p>
                    )}
                  </div>

                  <div className="border-t border-[#E5E5E5] pt-4">
                    <Label className="flex items-center justify-between">
                      <span>SMS Bakiyesi</span>
                      <span className="text-sm font-semibold text-[#1A1A2E]" data-testid="current-sms-balance">
                        Mevcut: {(planSmsEdit.sms_balance || 0).toLocaleString('tr-TR')}
                      </span>
                    </Label>
                    <div className="flex gap-2 mt-2">
                      <Select value={planSmsEdit.sms_mode} onValueChange={(v) => setPlanSmsEdit({ ...planSmsEdit, sms_mode: v })}>
                        <SelectTrigger className="w-28" data-testid="sms-mode-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="add">Ekle</SelectItem>
                          <SelectItem value="set">Ayarla</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        placeholder={planSmsEdit.sms_mode === 'add' ? 'Eklenecek SMS sayısı' : 'Yeni bakiye'}
                        value={planSmsEdit.sms_add}
                        onChange={(e) => setPlanSmsEdit({ ...planSmsEdit, sms_add: e.target.value })}
                        data-testid="sms-amount-input"
                      />
                    </div>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {[100, 500, 1000, 5000].map(n => (
                        <Button
                          key={n}
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => setPlanSmsEdit({ ...planSmsEdit, sms_mode: 'add', sms_add: String(n) })}
                          data-testid={`sms-quick-${n}`}
                        >
                          +{n}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={handleSavePlanSms}
                    className="w-full bg-[#1A1A2E] hover:bg-[#16213E] text-white"
                    data-testid="save-plan-sms-btn"
                  >
                    Kaydet
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Contract Edit Dialog */}
          <Dialog open={!!contractEdit} onOpenChange={(open) => !open && setContractEdit(null)}>
            <DialogContent className="max-w-sm" data-testid="contract-edit-dialog">
              <DialogHeader>
                <DialogTitle>Sözleşme Yönetimi</DialogTitle>
                <DialogDescription>{contractEdit?.name} için sözleşme süresini belirleyin.</DialogDescription>
              </DialogHeader>
              {contractEdit && (
                <div className="space-y-4">
                  <div><Label>Aylık Ücret (TL)</Label><Input type="number" value={contractEdit.monthly_fee} onChange={(e) => setContractEdit({ ...contractEdit, monthly_fee: e.target.value })} placeholder="0" data-testid="contract-fee-input" /></div>
                  <div><Label>Sözleşme Başlangıç</Label><Input type="date" value={contractEdit.contract_start} onChange={(e) => setContractEdit({ ...contractEdit, contract_start: e.target.value })} data-testid="contract-start-input" /></div>
                  <div><Label>Sözleşme Bitiş</Label><Input type="date" value={contractEdit.contract_end} onChange={(e) => setContractEdit({ ...contractEdit, contract_end: e.target.value })} data-testid="contract-end-input" /></div>
                  {contractEdit.contract_end && (() => {
                    const days = Math.ceil((new Date(contractEdit.contract_end) - new Date()) / 86400000);
                    return (
                      <div className={`p-3 rounded-lg text-sm ${days <= 0 ? 'bg-red-50 text-red-700' : days <= 15 ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>
                        {days <= 0 ? `Süre dolmuş (${Math.abs(days)} gün önce)` : `${days} gün kaldı`}
                      </div>
                    );
                  })()}
                  <Button onClick={handleSaveContract} className="w-full bg-orange-600 hover:bg-orange-700 text-white" data-testid="save-contract-btn">Sözleşmeyi Kaydet</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Record Payment Dialog */}
          <Dialog open={!!paymentRecord} onOpenChange={(open) => !open && setPaymentRecord(null)}>
            <DialogContent className="max-w-sm" data-testid="record-payment-dialog">
              <DialogHeader><DialogTitle>Ödeme Kaydet</DialogTitle><DialogDescription>{paymentRecord?.name} için ödeme kaydı.</DialogDescription></DialogHeader>
              {paymentRecord && (
                <div className="space-y-4">
                  <div><Label>Tutar (TL)</Label><Input type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="pay-amount-input" /></div>
                  <div><Label>Dönem</Label><Input type="month" value={payForm.period} onChange={(e) => setPayForm({ ...payForm, period: e.target.value })} data-testid="pay-period-input" /></div>
                  <div><Label>Yöntem</Label>
                    <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                      <SelectTrigger data-testid="pay-method-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="havale">Havale/EFT</SelectItem><SelectItem value="nakit">Nakit</SelectItem><SelectItem value="kart">Kredi Kartı</SelectItem><SelectItem value="diger">Diğer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Not</Label><Input value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} placeholder="Opsiyonel" data-testid="pay-notes-input" /></div>
                  <Button onClick={handleRecordPayment} className="w-full bg-[#2563EB] hover:bg-[#1E4ED8] text-white" data-testid="submit-payment-btn">Ödemeyi Kaydet</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Business Detail Dialog */}
          <Dialog open={!!detailView} onOpenChange={(open) => !open && setDetailView(null)}>
            <DialogContent className="max-w-lg" data-testid="detail-dialog">
              <DialogHeader><DialogTitle>{detailView?.business_name} - Detay</DialogTitle><DialogDescription>İşletme aktivite detayları</DialogDescription></DialogHeader>
              {detailView && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-blue-50 rounded-lg"><p className="text-xs text-blue-600">Tür</p><p className="font-medium text-sm">{typeLabels[detailView.business_type]}</p></div>
                    <div className="p-3 bg-emerald-50 rounded-lg"><p className="text-xs text-emerald-600">Aylık Ücret</p><p className="font-medium text-sm">{(detailView.subscription?.monthly_fee || 0).toLocaleString('tr-TR')} TL</p></div>
                  </div>
                  {detailView.owner && <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-[#595959]">Admin</p><p className="text-sm font-medium">{detailView.owner.name} ({detailView.owner.email})</p></div>}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 border rounded-lg text-center"><p className="text-2xl font-bold text-[#1A1A2E]">{detailView.stats.user_count}</p><p className="text-xs text-[#595959]">Kullanıcı</p></div>
                    <div className="p-3 border rounded-lg text-center"><p className="text-2xl font-bold text-blue-600">{detailView.stats.appointments_this_month}</p><p className="text-xs text-[#595959]">Randevu (Bu Ay)</p></div>
                    <div className="p-3 border rounded-lg text-center"><p className="text-2xl font-bold text-emerald-600">{detailView.stats.payments_this_month}</p><p className="text-xs text-[#595959]">Ödeme (Bu Ay)</p></div>
                    <div className="p-3 border rounded-lg text-center"><p className="text-2xl font-bold text-purple-600">{detailView.stats.messages_this_month}</p><p className="text-xs text-[#595959]">Mesaj (Bu Ay)</p></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-3 border rounded-lg"><p className="text-xs text-[#595959]">Toplam Randevu</p><p className="font-medium">{detailView.stats.appointments_total}</p></div>
                    <div className="p-3 border rounded-lg"><p className="text-xs text-[#595959]">Toplam Ödeme</p><p className="font-medium">{detailView.stats.payments_total}</p></div>
                    <div className="p-3 border rounded-lg"><p className="text-xs text-[#595959]">Toplam Mesaj</p><p className="font-medium">{detailView.stats.messages_total}</p></div>
                    <div className="p-3 border rounded-lg"><p className="text-xs text-[#595959]">Kayıt Tarihi</p><p className="font-medium">{detailView.created_at ? new Date(detailView.created_at).toLocaleDateString('tr-TR') : '-'}</p></div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Profile & Password Dialog */}
          <Dialog open={profileOpen} onOpenChange={(open) => { if (!open) setProfileOpen(false); }}>
            <DialogContent className="max-w-sm" data-testid="profile-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#2563EB]" /> Profil & Şifre Yönetimi</DialogTitle>
                <DialogDescription>Adınızı ve şifrenizi güncelleyin.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Ad Soyad</Label>
                  <Input value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} placeholder="Adınız" data-testid="profile-name-input" />
                </div>
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-[#1A1A2E] mb-3">Şifre Değiştir</p>
                  <div className="space-y-3">
                    <div>
                      <Label>Yeni Şifre</Label>
                      <Input type="password" value={profileForm.newPassword} onChange={(e) => setProfileForm({ ...profileForm, newPassword: e.target.value })} placeholder="En az 6 karakter" data-testid="profile-new-password" />
                    </div>
                    <div>
                      <Label>Yeni Şifre (Tekrar)</Label>
                      <Input type="password" value={profileForm.confirmPassword} onChange={(e) => setProfileForm({ ...profileForm, confirmPassword: e.target.value })} placeholder="Şifreyi tekrar girin" data-testid="profile-confirm-password" />
                    </div>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-[#1A1A2E] mb-3">İki Faktörlü Kimlik Doğrulama (2FA)</p>
                  <TwoFactorSection />
                </div>
                <Button onClick={handleSaveProfile} className="w-full bg-[#2563EB] hover:bg-[#1E4ED8] text-white" data-testid="save-profile-btn">Kaydet</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
};
